import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { Protected } from "@/components/Protected";
import AppLayout from "@/components/AppLayout";
import Home from "@/pages/Home";
import Apply from "@/pages/Apply";
import { Login, Register } from "@/pages/Auth";
import AuthCallback from "@/pages/auth/AuthCallback";
import Invite from "@/pages/auth/Invite";
import { ForgotPassword, ResetPassword } from "@/pages/auth/PasswordReset";
import Settings from "@/pages/Settings";
import Kiosk from "@/pages/Kiosk";
import CalendarPage from "@/pages/CalendarPage";
import Reports from "@/pages/admin/Reports";
import Staff from "@/pages/admin/Staff";
import Payroll from "@/pages/admin/Payroll";
import AdminStatements from "@/pages/admin/AdminStatements";
import Statements from "@/pages/parent/Statements";
import ParentForms from "@/pages/parent/ParentForms";
import SignForm from "@/pages/parent/SignForm";
import SignedDocument from "@/pages/SignedDocument";
import PublicCheckin from "@/pages/PublicCheckin";
import PublicForm from "@/pages/PublicForm";
import Forms from "@/pages/admin/Forms";
import DaySheet from "@/pages/DaySheet";
import Album from "@/pages/parent/Album";
import Overview from "@/pages/admin/Overview";
import Attendance from "@/pages/admin/Attendance";
import AttendanceLog from "@/pages/admin/AttendanceLog";
import TeacherTablet from "@/pages/admin/TeacherTablet";
import Students from "@/pages/admin/Students";
import StudentProfile from "@/pages/admin/StudentProfile";
import Classrooms from "@/pages/admin/Classrooms";
import AuditLog from "@/pages/admin/AuditLog";
import Permissions from "@/pages/admin/Permissions";
import Families from "@/pages/admin/Families";
import { INTERNAL_ROLES, STAFF_ROLES, FINANCE_ROLES, PAYROLL_ROLES, ADMIN_ROLES } from "@/lib/roles";
import Admissions from "@/pages/admin/Admissions";
import Billing from "@/pages/admin/Billing";
import Expenses from "@/pages/admin/Expenses";
import Finance from "@/pages/admin/Finance";
import WebsiteEditor from "@/pages/admin/WebsiteEditor";
import Tasks from "@/pages/admin/Tasks";
import Messages from "@/pages/admin/Messages";
import ParentHome from "@/pages/parent/ParentHome";
import ParentDashboard from "@/pages/parent/ParentDashboard";
import ParentRequests from "@/pages/parent/ParentRequests";
import PortalControls from "@/pages/admin/PortalControls";
import RosterImport from "@/pages/admin/RosterImport";
import ParentBilling from "@/pages/parent/ParentBilling";
import ParentMessages from "@/pages/parent/ParentMessages";
import Careers from "@/pages/careers/Careers";
import PositionDetail from "@/pages/careers/PositionDetail";
import ApplyStart from "@/pages/careers/ApplyStart";
import ApplicationWorkspace from "@/pages/careers/ApplicationWorkspace";
import Hiring from "@/pages/admin/Hiring";
import { PaymentSuccess, PaymentCancel } from "@/pages/Payment";

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/apply" element={<Apply />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/careers/apply/:pid" element={<ApplyStart />} />
          <Route path="/careers/application/:token" element={<ApplicationWorkspace />} />
          <Route path="/careers/:pid" element={<PositionDetail />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/invite/:token" element={<Invite />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/checkin" element={<PublicCheckin />} />
          <Route path="/forms/:formId" element={<PublicForm />} />
          <Route path="/kiosk" element={
            <Protected roles={STAFF_ROLES}><Kiosk /></Protected>
          } />

          <Route path="/admin" element={
            <Protected roles={INTERNAL_ROLES}><AppLayout variant="admin" /></Protected>
          }>
            <Route index element={<Overview />} />
            <Route path="attendance" element={<Attendance />} />
            <Route path="attendance-log" element={<Protected roles={STAFF_ROLES}><AttendanceLog /></Protected>} />
            <Route path="classroom" element={<Protected roles={STAFF_ROLES}><TeacherTablet /></Protected>} />
            <Route path="students" element={<Students />} />
            <Route path="students/:studentId" element={<StudentProfile />} />
            <Route path="families" element={<Protected roles={ADMIN_ROLES}><Families /></Protected>} />
            <Route path="portal" element={<Protected roles={STAFF_ROLES}><PortalControls /></Protected>} />
            <Route path="import" element={<Protected roles={ADMIN_ROLES}><RosterImport /></Protected>} />
            <Route path="classrooms" element={<Classrooms />} />
            <Route path="audit" element={<AuditLog />} />
            <Route path="permissions" element={<Protected roles={ADMIN_ROLES}><Permissions /></Protected>} />
            <Route path="admissions" element={<Admissions />} />
            <Route path="messages" element={<Messages />} />
            <Route path="forms" element={<Forms />} />
            <Route path="signed/:sigId" element={<SignedDocument />} />
            <Route path="daysheet/:studentId" element={<DaySheet />} />
            <Route path="calendar" element={<CalendarPage />} />
            <Route path="staff" element={<Staff />} />
            <Route path="hiring" element={<Hiring />} />
            <Route path="reports" element={<Reports />} />
            <Route path="finance" element={<Finance />} />
            <Route path="website" element={<WebsiteEditor />} />
            <Route path="tasks" element={<Tasks />} />
            <Route path="payroll" element={<Protected roles={PAYROLL_ROLES}><Payroll /></Protected>} />
            <Route path="statements" element={<Protected roles={FINANCE_ROLES}><AdminStatements /></Protected>} />
            <Route path="billing" element={<Protected roles={FINANCE_ROLES}><Billing /></Protected>} />
            <Route path="expenses" element={<Protected roles={FINANCE_ROLES}><Expenses /></Protected>} />
            <Route path="settings" element={<Settings />} />
          </Route>

          <Route path="/portal" element={
            <Protected roles={["parent"]}><AppLayout variant="parent" /></Protected>
          }>
            <Route index element={<ParentDashboard />} />
            <Route path="history" element={<ParentHome />} />
            <Route path="requests" element={<ParentRequests />} />
            <Route path="children/:studentId" element={<StudentProfile portal />} />
            <Route path="album" element={<Album />} />
            <Route path="daysheet/:studentId" element={<DaySheet />} />
            <Route path="calendar" element={<CalendarPage />} />
            <Route path="messages" element={<ParentMessages />} />
            <Route path="billing" element={<ParentBilling />} />
            <Route path="statements" element={<Statements />} />
            <Route path="forms" element={<ParentForms />} />
            <Route path="forms/:formId/sign" element={<SignForm />} />
            <Route path="signed/:sigId" element={<SignedDocument />} />
            <Route path="settings" element={<Settings />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
